/*

fix file listing for polar and race, doesn't seem to skip items in trash
bug when removing first column of polar

*/
import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PathLayer } from '@deck.gl/layers';
import * as c from "../common.js";
import { DataStore } from "../domain/DataStore.js";
import { Race, Sail } from "../domain/Race.js";
import { Info } from "../domain/Info.js";
import { Polar } from "../domain/Polar.js";
import * as gd from "../utils/drive-helpers.js";
import * as polar from "./polar-plotly.js";
import deleteIconUrl from '../../icons/delete.svg?url';

/* fileRec example
loaded:false,
raceName: '2025-port-underwood' ,
annotationsInUse: Set (),
raceFile: {
    "id": "1f1kTCUbvkpABVY4FDlDl9kLsh9yMSV8z",
    "name": "2025-port-underwood.json",
    "mimeType": "application/json"
},
infoFile: {
    "id": "1f1kTCUbvkpABVY4FDlDl9kLsh9yMSV8z",
    "name": "__info.2025-port-underwood.json",
    "mimeType": "application/json"
},
*/

const model = {
    _init:false,
    _raceFiles:[], // an array of fileRec
    showHalfPolar:true,
    showDotsOnPolar:false,
    polar:null,
    raceTimeframes: null,
    polarFileId:null,
    _polarfileCache: new Map(),
    selectedPolarName: null,
    showingPolarFile: false,
    overlayPolar: null,

    init: async function() {
        if (!this._init) {
            await gd.ensurePolarFolderAsync();
            this._init = true;
            return false;
        }
        return this._init;
    },
    setSelectedPolarName: function(name) {
        this.selectedPolarName = name;
    },
    setOverlayPolar: function(polar) {
        this.overlayPolar = polar;
    },
    getOverlayPolar: function() {
        return this.overlayPolar;
    },
    getSelectedPolarCacheItemAsync: async function() {
        return this.getNamedPolarCacheItemAsync(this.selectedPolarName);
    },
    getNamedPolarCacheItemAsync: async function(name) {
        let polarCached = this._polarfileCache.get(name);
        if (polarCached == undefined) {
            polarCached = await this.loadPolarAsync(name);
        }
        return polarCached;
    },    
    getCachedPolarFilesAsync: async function() {
        return this._polarfileCache;
    },
    
    loadPolarAsync: async function (name) {
        let item = {
            filename: name,
            saved: true,
            polar: null
        }
        try {
            //TODO - change to use DataStore.loadPolarFromGDriveById - quicker
            const polarObj = await DataStore.loadPolarByNameAsync(name);
            if (polarObj) {
                item.polar = polarObj.polarStruct;
                item.id = polarObj.fileId;
            }
        } catch (e) {
            console.log(e);
        }
        this._polarfileCache.set(name, item);
        console.log('loaded: polar',name);
        return item;
    },
    deletePolarAsync: async function(name) {
        let polarCached = this._polarfileCache.get(name);
        if (polarCached != undefined) {
            await gd.deleteFileAsync(polarCached.id);
        }
    },
    loadRaceList: async function(loadRaceFn, loadAnnotationsFn) {
        const result = await gd.listFolderContentsAsync(gd.getDataFolder());
        const resultInfo = await gd.listFolderContentsAsync(await gd.ensureAppDataFolderAsync());
        if (result != null && result.files != undefined && resultInfo != null && resultInfo.files != undefined) {
            result.files.push(...resultInfo.files);
            const races = DataStore.extractRaceFiles(result.files);
            if (races != null && races.length > 0) {
                this._raceFiles = races;
                for(const fileRec of this._raceFiles) {
                    this.loadFileAnnotations(fileRec, loadRaceFn, loadAnnotationsFn);
                }
            } else {
                alert('No race files with corresponding labeled sections found');
            }
        } else {
            alert('No race files found.');
        }
        console.log("Race list:",this._raceFiles);
    },
    updatePolarFromSelection: function() {
        const timeframes = [];
        for (const fileRec of this._raceFiles) {
            if (fileRec.annotationsInUse != undefined) {
                fileRec.annotationsInUse.forEach(x => {
                    timeframes.push(...sliceTimeframesBy(fileRec.vessel.data, x));
                });
            }
        }
        this.raceTimeframes = timeframes;
        this.racePolar = c.createPolar(timeframes, true).polarStruct;
    },
    savePolarAs: async function(fileName) {
        if (!fileName.toLocaleLowerCase().endsWith(".polar.json")) {
            fileName = fileName+".polar.json";
        }
        let polarStruct;
        if (this.isPolarFileShowing()) {
            const item = await this.getSelectedPolarCacheItemAsync();
            polarStruct = item.polar;
        } else {
            let races = [];
            for(let r of this._raceFiles) {
                if (r.annotationsInUse != null && r.annotationsInUse.size > 0) {
                    races.push({
                        annotationsInUse: r.annotationsInUse,
                        raceName: r.raceName,
                    });
                }
            }
            console.log(races);
            polarStruct = this.racePolar;
            polarStruct.races = races;
        }
        const polar = new Polar(polarStruct, fileName, null);
        await c.dataStore.savePolarToGDrive(polar, c.setLoadingMessage);
        this.racePolarFileId = polar.fileId;
    },
    saveSelectedPolar: async function() {
        const item = await this.getSelectedPolarCacheItemAsync();
        const polar = new Polar(item.polar, item.filename, item.id);
        await c.dataStore.savePolarToGDrive(polar, c.setLoadingMessage);
    },
    loadPolarAsyncList: async function() {
        const polarFolder = await gd.ensurePolarFolderAsync();
        let result = await gd.listFolderContentsAsync(polarFolder);
        if (result == null || result.files == undefined)
            return [];

        const files = result.files.filter(f => f.name.endsWith(".polar.json"));
        for (const f of files) {
            this.loadPolarAsync(f.name);
        }
        return files;
    },
    async loadFileAnnotations(fileRec, loadRaceFn, whenLoadedFn) {
        const div = loadRaceFn(fileRec);
        const info = await DataStore.loadInfoFromGDriveById(fileRec.infoFile.id, fileRec.infoFile.name);
        if (info != null) {
            fileRec.raceInfo = info;
            info.orderRaceInfoAnnotations();
            fileRec.annotationSequence = info.getAnnotationSequence();
            whenLoadedFn(div, fileRec);
        }
    },
    setShowingPolarFile: function(flag) {
        this.showingPolarFile=flag;
    },
    isPolarFileShowing: function() {
        return this.showingPolarFile;
    }

};

const PLOTLY_DIV = 'pb-polar-plot';

let map;
let overlay;

export async function showAsync() {
    const initAlready = await model.init();
    if (!initAlready) {
        map = new maplibregl.Map({
            container: 'pb-map',
            style: {
                version: 8,
                sources: {
                    'osm': {
                        type: 'raster',
                        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                        tileSize: 256,
                        maxzoom: 19
                    }
                },
                layers: [{
                    id: 'osm',
                    type: 'raster',
                    source: 'osm'
                }]
            },
            center: [-98, 38],
            zoom: 2,
            maxZoom: 19
        });
        overlay = new MapboxOverlay({ layers: [] });
        map.addControl(overlay);

        loadRaceListHtmlAsync();
        loadPolarAsyncListHtml();
        initTableArrowListener();
    }
    if (c.raceData() != null) {
        const data = c.raceData().sail().data;
        if (data != null) {
            showTrack(data.vessel);
            showSelectedRacePolar();
        }
    }
}

/***************************************** UI STATE LOGIC ******************************************/

const polarDataLeft = document.getElementById("polarDataLeft");
polarDataLeft.addEventListener("click", () => {
    showSelectedRacePolar();
    flagPolarSaved(false);
});

const polarDataRight = document.getElementById("polarDataRight");
polarDataRight.addEventListener("click", async () => {
    await showSelectedPolarFileAsync();
});

function showSelectedRacePolar() {
    if (model.racePolar != null) {
        model.setShowingPolarFile(false);
        showPolarComponents(model.racePolar, null, model.raceTimeframes, "< not saved yet >", false);
        setButtonState();
    }
}

async function showSelectedPolarFileAsync() {
    const item = await model.getSelectedPolarCacheItemAsync();
    const overlayPolar = model.getOverlayPolar()
    const polar = item.polar;
    if (polar != null) {
        model.setShowingPolarFile(true);
        showPolarComponents(polar, overlayPolar, null,item.filename, item.saved);
        setButtonState();
    }
}

function setButtonState() {
    if (model.isPolarFileShowing()) {
        polarDataRight.classList.add("selected");
        polarDataLeft.classList.remove("selected");
        polarBuilderSaveButton.style.display="block";
    } else {
        polarDataLeft.classList.add("selected");
        polarDataRight.classList.remove("selected");
        polarBuilderSaveButton.style.display="none";
    }
}


function showPolarComponents(p, p2, timeframes, polarName, polarSaved) {
    showPolar(p,p2, timeframes);
    showTable(p, polarName, polarSaved);
}

function showPolar(p, p2, timeframes) {
    polar.plotPolarMap(p, p2, timeframes, PLOTLY_DIV, model.showHalfPolar, model.showDotsOnPolar, null, false);
}


/***************************************** RACE LISTING ******************************************/

async function loadRaceListHtmlAsync() {
    const sidebar = document.getElementById('pb-sidebar');
    model.loadRaceList(
        (fileRec) => {
            const fileDiv = document.createElement('div');
            fileDiv.className = "pb-filename";
            fileDiv.textContent = fileRec.raceName;
            fileDiv.id = `fileDiv-${fileRec.raceName}`;
            sidebar.appendChild(fileDiv);
            return fileDiv;
        },
        (fileDiv, fileRec) => {
            for(const a of fileRec.annotationSequence) {
                const aDiv = document.createElement('div');
                aDiv.className = 'pb-section';
                aDiv.textContent = summarizeAnnotation(a);
                aDiv.addEventListener("click", (e) => {
                    loadAndHighlightTrackSegmentAsync(aDiv, fileRec, a);
                });
                fileDiv.appendChild(aDiv);
            }
        });
}

function summarizeAnnotation(a) {
    let result = '- ';
    for(let i=0;i<a.labels.length;i++) {
        result += a.labels[i];
        if (i < a.labels.length - 1) {
            result += '/';
        }
    }
    result += a.notes == null || a.notes.length == 0? '' : ` (${a.notes})`;
    return result;
}

async function loadAndHighlightTrackSegmentAsync(thisDiv, fileRec, annotation) {
    if (!fileRec.loaded) {
        if (fileRec.vessel == undefined) {
            try {
                c.showLoadingSpinner();
                const sail = await DataStore.loadSailFromGDriveById(fileRec.raceFile.id, fileRec.raceFile.name);
                fileRec.vessel = sail.data.vessel;
                const fileDiv = document.getElementById(`fileDiv-${fileRec.raceName}`);
                const divs = fileDiv.querySelectorAll('div');
                divs.forEach(div => {
                    div.classList.add('loaded');
                    div.addEventListener("hover", (e) => {
                        loadAndHighlightTrackSegmentAsync(thisDiv, file, annotation);
                    });
                });
            } finally {
                c.hideLoadingSpinner();
            }
        }
    }
    
    if (fileRec.annotationsInUse == undefined) {
        fileRec.annotationsInUse = new Set();
    }
    if (fileRec.annotationsInUse.has(annotation)) {
        thisDiv.classList.remove('selected');
        fileRec.annotationsInUse.delete(annotation);
    } else {
        thisDiv.classList.add('selected');
        fileRec.annotationsInUse.add(annotation);
    }

    fileRec.loaded = true;
    showTrack(fileRec.vessel, annotation);
    model.updatePolarFromSelection();
    showSelectedRacePolar();
}



/***************************************** POLAR LISTING ******************************************/

async function loadPolarAsyncListHtml() {
    const list = document.getElementById('pb-polar-list-container');
    list.innerText = '';
    const files = await model.loadPolarAsyncList();
    for (const f of files) {
        const fileDiv = document.createElement('div');
        fileDiv.classList.add('form-row-no-border');
        fileDiv.classList.add("pb-section");
        fileDiv.classList.add('loaded');
        const div = document.createElement('div');
        div.innerText = f.name;
        fileDiv.append(div);
        const delDiv = document.createElement("div");
        delDiv.className = "icon-button";
        const img = document.createElement('img');
        img.src = deleteIconUrl;
        delDiv.appendChild(img);
        delDiv.addEventListener("click", async () => {
            await model.deletePolarAsync(f.name);
            loadPolarAsyncListHtml();
        });
        fileDiv.appendChild(delDiv);
        fileDiv.addEventListener("click", async  e => {
            list.querySelectorAll('div').forEach(d => d.classList.remove("selected"));
            fileDiv.classList.add("selected");
            model.setSelectedPolarName(f.name);
            try {
                c.showLoadingSpinner();
                await showSelectedPolarFileAsync();
                const item = await model.getSelectedPolarCacheItemAsync();
                item.saved = true;
            } finally {
                c.hideLoadingSpinner();
            }
        });
        list.appendChild(fileDiv);
    }
}

/***************************************** POLAR SEGMENT MAP ******************************************/

function showTrack(vessel, annotation) {
    const coords = vessel.data.map(f => [f.lon, f.lat]);
    const layers = [
        new PathLayer({
            id: 'track',
            data: [{ path: coords }],
            getPath: d => d.path,
            getColor: [128, 128, 128],
            getWidth: 2,
            widthMinPixels: 2
        })
    ];
    
    if (annotation) {
        const highlightCoords = sliceCoordsForAnnotation(vessel.data, annotation);
        if (highlightCoords) {
            layers.push(new PathLayer({
                id: 'highlight',
                data: [{ path: highlightCoords }],
                getPath: d => d.path,
                getColor: [255, 0, 0],
                getWidth: 2,
                widthMinPixels: 2
            }));
        }
    }
    
    overlay.setProps({ layers });
    
    setTimeout(() => {
        const bounds = coords.reduce((b, c) => {
            b[0] = Math.min(b[0], c[0]);
            b[1] = Math.min(b[1], c[1]);
            b[2] = Math.max(b[2], c[0]);
            b[3] = Math.max(b[3], c[1]);
            return b;
        }, [Infinity, Infinity, -Infinity, -Infinity]);
        map.fitBounds(bounds, { padding: 40 });
    }, 200);
}

function sliceTimeframesBy(timeframes, annotation) {
    const startIdx = timeframes.findIndex(x => x.t.isSameOrAfter(annotation.begin_ts));
    const endIdx = timeframes.findIndex(x => x.t.isSameOrAfter(annotation.end_ts));
    if (startIdx >= 0 && endIdx >= 0) {
        return timeframes.slice(startIdx,endIdx+1);
    }
    return null;
}

function sliceCoordsForAnnotation(data, annotation) {
    const startIdx = data.findIndex(x => x.t.isSameOrAfter(annotation.begin_ts));
    const endIdx = data.findIndex(x => x.t.isSameOrAfter(annotation.end_ts));
    if (startIdx >= 0 && endIdx >= 0) {
        return data.slice(startIdx, endIdx + 1).map(f => [f.lon, f.lat]);
    }
    return null;
}

/***************************************** POLAR EDITOR ******************************************/

function setPolarName(polarName) {
    const title = document.getElementById("polarEditorTitle");
    title.innerHTML = polarName;
}

function showTable(polarStruct, polarName, polarSaved) {
    setPolarName(polarName);
    flagPolarSaved(polarSaved);
    const table = document.getElementById("pb-table");
    table.innerHTML = '';
    const thead = document.createElement('thead');
    let th = document.createElement('th');
    th.innerText='';
    th.className='pb-th';
    thead.appendChild(th);
    polarStruct.tws.forEach(tws => {
        th = document.createElement('th');
        th.innerText=tws;
        th.className='pb-th';
        thead.appendChild(th);
    });
    table.appendChild(thead);
    // const windSpeed = polarStruct.tws[windIdx];
    for(let angleIdx=0;angleIdx < polarStruct.twa.length;angleIdx++) {
        let tr = document.createElement('tr');
        let td = document.createElement('td');
        td.className='pb-td';
        td.innerText = polarStruct.twa[angleIdx].twa;
        tr.appendChild(td);
        for (let windIdx=0;windIdx<polarStruct.tws.length;windIdx++) {
            td = document.createElement('td');
            td.className='pb-td';
            const input = document.createElement('input');
            input.type='text';
            input.value=polarStruct.twa[angleIdx].boatSpeed[windIdx];
            input.addEventListener("focus", (e) => {
                polar.pointHighlight(PLOTLY_DIV, polarStruct.twa[angleIdx].twa, polarStruct.twa[angleIdx].boatSpeed[windIdx]);
            });
            input.addEventListener("blur", (e) => {
                polar.clearPointHighlight(PLOTLY_DIV);
            });

            input.addEventListener("change", async (e) => {
                // angleIdx windIdx
                polarStruct.twa[angleIdx].boatSpeed[windIdx] = e.target.value;
                showPolar(polarStruct, model.getOverlayPolar());
                const item = await model.getNamedPolarCacheItemAsync(polarName);
                item.saved = false;
                flagPolarSaved(false);
            });
            td.appendChild(input);
            tr.appendChild(td);
        }
        table.appendChild(tr);
    }
}

function flagPolarSaved(status) {
    if (status)
        document.getElementById('polarEditorTitle').classList.remove('polar-title-unsaved');
    else
        document.getElementById('polarEditorTitle').classList.add('polar-title-unsaved');
}

function initTableArrowListener() {
    const table = document.getElementById('pb-table');

  table.addEventListener('keydown', function (e) {
    const key = e.key;
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;

    const current = e.target;
    if (current.tagName !== 'INPUT') return;

    const td = current.closest('td');
    const tr = current.closest('tr');
    if (!td || !tr) return;

    const cellIndex = td.cellIndex;
    const rowIndex = tr.rowIndex;

    const rows = Array.from(table.rows);
    let target;

    switch (key) {
      case 'ArrowRight':
        target = td.nextElementSibling?.querySelector('input');
        break;
      case 'ArrowLeft':
        target = td.previousElementSibling?.querySelector('input');
        break;
      case 'ArrowUp':
        if (rowIndex > 0) {
          target = rows[rowIndex - 1].cells[cellIndex]?.querySelector('input');
        }
        break;
      case 'ArrowDown':
        if (rowIndex < rows.length - 1) {
          target = rows[rowIndex + 1].cells[cellIndex]?.querySelector('input');
        }
        break;
    }

    if (target) {
      e.preventDefault(); // prevent cursor move in text field
      target.focus();
    }
  });
}

const polarBuilderSaveAs = document.getElementById("polarBuilderSaveAs");
polarBuilderSaveAs.addEventListener("click", async () => {
    let fileName = prompt(
        "Enter the file name to save:",
        ""
    );
    if (!fileName) {
        alert("File name is required to save the file.");
        return;
    }
    try {
        c.saveButtonSpinnerOn(polarBuilderSaveAs);
        await model.savePolarAs(fileName);
        setPolarName(fileName);
        flagPolarSaved(true);
    } catch (error) {
        console.error("Error saving file:", error);
        alert("An error occurred while saving the file." + error.message);
    } finally {
        c.saveButtonSpinnerOff(polarBuilderSaveAs);
    }
    model.loadPolarAsyncList();
    loadPolarAsyncListHtml();
});
const polarBuilderSaveButton = document.getElementById("polarBuilderSave");
polarBuilderSaveButton.addEventListener("click", async () => {
    try {
        c.saveButtonSpinnerOn(polarBuilderSaveButton);
        model.saveSelectedPolar();
        flagPolarSaved(true);
    } finally {
        c.saveButtonSpinnerOff(polarBuilderSaveButton);
    }
});

document.getElementById("polarBuilderExport").addEventListener("click", async () => {
    // Get the currently selected polar
    const item =await model.getSelectedPolarCacheItemAsync();
    const polarData = item.polar;
    if (!polarData) {
        alert("No polar selected.");
        return;
    }
    const polarText = printPolarAsCSV(polarData);

    const popup = window.open("", "Polar Export", "width=800,height=600");
    if (popup) {
        popup.document.open(); // Clear previous content
        popup.document.write("<pre style='white-space:pre-wrap;word-break:break-all;'>" + 
            polarText + "</pre>");
        popup.document.title = "Polar JSON Export";
        popup.document.close();
    } else {
        alert("Popup blocked. Please allow popups for this site.");
    }
});

function printPolarAsCSV(polarStruct) {
    let csv = 'TWA/TWS';
    polarStruct.tws.forEach(tws => {
        csv += ',' + tws;
    });
    csv += '\n';
    for (let angleIdx = 0; angleIdx < polarStruct.twa.length; angleIdx++) {
        let row = polarStruct.twa[angleIdx].twa;
        for (let windIdx = 0; windIdx < polarStruct.tws.length; windIdx++) {
            let v = polarStruct.twa[angleIdx].boatSpeed[windIdx];
            v = v == null ? '':v;
            row += ',' + v;
        }
        csv += row + '\n';
    }
    return csv;
}

/***************************************** POLAR PLOT ******************************************/

document.getElementById('halfPolarToggle').addEventListener("click", (e) => {
    model.showHalfPolar = !model.showHalfPolar;
    showSelectedRacePolar();
});

document.getElementById('polarBuilderDotsToggle').addEventListener("click", (e) => {
    model.showDotsOnPolar = !model.showDotsOnPolar;
    showSelectedRacePolar();
});

const polarBuilderOverlayButton = document.getElementById("polarBuilderOverlay");
polarBuilderOverlayButton.addEventListener("click", async () => {
    if (model.getOverlayPolar() != null) {
        polarBuilderOverlayButton.classList.remove("selected");
        model.setOverlayPolar(null);
        showSelectedPolarFileAsync();
        return;
    }
    
    polarBuilderOverlayButton.classList.add("selected");
    const item1 = await model.getSelectedPolarCacheItemAsync();
    const polar1 = item1.polar;
    if (!polar1) {
        alert("No polar selected.");
        return;
    }

    const polarCache = await model.getCachedPolarFilesAsync();
    const names = Array.from(polarCache.keys()).filter(n => n !== model.selectedPolarName);
    if (names.length === 0) {
        alert("No other polar files available for overlay.");
        return;
    }

    // Create a simple modal dialog for selection
    const modal = document.createElement("div");
    modal.style.position = "fixed";
    modal.style.top = "0";
    modal.style.left = "0";
    modal.style.width = "100vw";
    modal.style.height = "100vh";
    modal.style.background = "rgba(0,0,0,0.5)";
    modal.style.display = "flex";
    modal.style.alignItems = "center";
    modal.style.justifyContent = "center";
    modal.style.zIndex = "9999";

    const box = document.createElement("div");
    box.style.background = "#fff";
    box.style.padding = "24px";
    box.style.borderRadius = "8px";
    box.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
    box.innerHTML = "<h3>Select a polar file to overlay:</h3>";
    names.forEach(name => {
        const btn = document.createElement("button");
        btn.textContent = name;
        btn.style.display = "block";
        btn.style.margin = "8px 0";
        btn.onclick = async () => {
            document.body.removeChild(modal);
            model.setOverlayPolar(polarCache.get(name).polar);
            showSelectedPolarFileAsync();
        };
        box.appendChild(btn);
    });

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.marginTop = "16px";
    cancelBtn.onclick = () => {
        document.body.removeChild(modal);
    };
    box.appendChild(cancelBtn);
    modal.appendChild(box);
    document.body.appendChild(modal);
});